import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, CornerLeftUp } from 'lucide-react';
import { CARD_CLASS, EYEBROW_CLASS, OMS_COLORS } from '../omsDashTheme';
import type { OmsResourcesOverview } from '../types';
import {
  UNMAPPED_LOCATION_ID,
  buildEngineers,
  byScoreDesc,
  type EngineerPerf,
  type PerfNode,
  type PerformanceModel,
} from '../utils/performanceModel';
import type { ReportPayload } from '../utils/exportReport';
import { deltaPhrase as deltaPhraseOf } from '../utils/scopeWindow';
import type { Scope, ScopeActions, ScopeLevel } from '../hooks/useScope';
import { formatNumber } from '../utils/formatNumber';
import { BandChip, DeltaChip, ScoreDial, ScoreExplainer } from './HealthScore';
import StatCount from './StatCount';
import LocationCards from './LocationCards';
import ProjectLeaderboard from './ProjectLeaderboard';
import EngineerLeaderboard from './EngineerLeaderboard';
import PerfRails from './PerfRails';
import CompareTray from './CompareTray';
import ExportMenu from './ExportMenu';
import ScopedDailyReportButton from './ScopedDailyReportButton';

type SortKey = 'score' | 'overdue' | 'completion';

const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
  { value: 'score', label: 'Health score' },
  { value: 'overdue', label: 'Most overdue' },
  { value: 'completion', label: 'Completion rate' },
];

const LEVEL_LABEL: Record<ScopeLevel, string> = {
  1: 'States',
  2: 'Locations',
  3: 'Projects',
  4: 'Site engineers',
};

interface Props {
  scope: Scope;
  actions: ScopeActions;
  model: PerformanceModel;
  current: OmsResourcesOverview | null | undefined;
  previous: OmsResourcesOverview | null | undefined;
  loading: boolean;
  onOpenEngineer: (engineer: EngineerPerf) => void;
  onOpenProject: (projectId: string) => void;
  onReport: (payload: ReportPayload) => void;
  /** Manager/Head only — shows the scope-aware "Daily report" download. */
  showDailyReport?: boolean;
}

function sortRows<T extends PerfNode>(rows: T[], sort: SortKey): T[] {
  const copy = [...rows];
  if (sort === 'score') copy.sort(byScoreDesc);
  if (sort === 'overdue') copy.sort((a, b) => b.overdue - a.overdue || byScoreDesc(a, b));
  if (sort === 'completion')
    copy.sort(
      (a, b) =>
        (b.scheduled > 0 ? b.completed / b.scheduled : -1) -
        (a.scheduled > 0 ? a.completed / a.scheduled : -1)
    );
  return copy;
}

function railsOf(rows: PerfNode[]): { top: PerfNode[]; bottom: PerfNode[] } {
  const scored = [...rows].sort(byScoreDesc).filter((r) => r.score !== null);
  const top = scored.slice(0, 3);
  const topIds = new Set(top.map((n) => n.id));
  const bottom = scored
    .filter((n) => !topIds.has(n.id) && (n.score!.band === 'watch' || n.score!.band === 'attention'))
    .slice(-3)
    .reverse();
  return { top, bottom };
}

/**
 * The Performance Explorer — the dashboard's centerpiece. Four levels of
 * one drill-down (India → state → location → project → engineers; one
 * location can hold several projects, and managers track progress
 * location-wise) with ranked rows, Top-performers / Needs-attention rails,
 * compare mode and WYSIWYG export at every level. The signature moment:
 * the plate you click morphs into the header of the level you land on.
 */
export default function PerformanceExplorer({
  scope,
  actions,
  model,
  current,
  previous,
  loading,
  onOpenEngineer,
  onOpenProject,
  onReport,
  showDailyReport = false,
}: Props) {
  const [sort, setSort] = useState<SortKey>('score');
  const [expanded, setExpanded] = useState(false);
  const [compareIds, setCompareIds] = useState<Set<string>>(new Set());
  const phrase = deltaPhraseOf(scope.preset);

  // Compare selections and expansion are per-level concerns.
  const levelKey = `${scope.level}-${scope.state ?? ''}-${scope.locationId ?? ''}-${scope.projectId ?? ''}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on drill
  useEffect(() => {
    setCompareIds(new Set());
    setExpanded(false);
  }, [levelKey]);

  const stateNode = scope.state ? model.states.find((s) => s.name === scope.state) ?? null : null;
  const locationNode = scope.locationId
    ? model.locations.find((l) => l.id === scope.locationId) ?? null
    : null;
  const projectNode = scope.projectId ? model.projects.find((p) => p.id === scope.projectId) ?? null : null;

  const engineers = useMemo(() => {
    if (scope.level !== 4 || !scope.projectId) return [];
    return buildEngineers(current ?? null, previous ?? null, new Set([scope.projectId]));
  }, [scope.level, scope.projectId, current, previous]);

  const q = scope.query.trim().toLowerCase();
  const matches = useCallback((name: string) => !q || name.toLowerCase().includes(q), [q]);

  const rows: PerfNode[] = useMemo(() => {
    if (scope.level === 1) return sortRows(model.states.filter((s) => matches(s.name)), sort);
    if (scope.level === 2)
      return sortRows(
        model.locations.filter((l) => l.stateName === scope.state && matches(l.name)),
        sort
      );
    if (scope.level === 3)
      return sortRows(
        model.projects.filter(
          (p) => (p.locationId ?? UNMAPPED_LOCATION_ID) === scope.locationId && matches(p.name)
        ),
        sort
      );
    return sortRows(engineers.filter((e) => matches(e.name)), sort);
  }, [scope.level, scope.state, scope.locationId, model, engineers, sort, matches]);

  const rails = useMemo(() => railsOf(rows), [rows]);

  const scopePath = useMemo(() => {
    const path = ['India'];
    if (scope.state) path.push(scope.state);
    if (locationNode) path.push(locationNode.name);
    else if (scope.locationId) path.push('Location');
    if (projectNode) path.push(projectNode.name);
    else if (scope.projectId) path.push('Project');
    return path;
  }, [scope.state, scope.locationId, scope.projectId, locationNode, projectNode]);

  const buildReport = useCallback(
    (): ReportPayload => ({
      scopePath,
      levelLabel: LEVEL_LABEL[scope.level],
      window: scope.window,
      deltaPhrase: phrase,
      rows,
      topPerformers: rails.top,
      needsAttention: rails.bottom,
    }),
    [scopePath, scope.level, scope.window, phrase, rows, rails]
  );

  useEffect(() => {
    onReport(buildReport());
  }, [buildReport, onReport]);

  // Keyboard navigation: arrows move through rows/cards, Enter drills
  // (native button behaviour), Esc/Backspace goes up a level.
  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    if (/^(INPUT|SELECT|TEXTAREA)$/.test(target.tagName)) return;
    const container = e.currentTarget;
    if (e.key === 'Escape' || e.key === 'Backspace') {
      if (scope.level > 1) {
        e.preventDefault();
        actions.drillUp();
      }
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'].includes(e.key)) return;
    const rowsEls = Array.from(container.querySelectorAll<HTMLElement>('[data-perf-row]'));
    if (rowsEls.length === 0) return;
    e.preventDefault();
    const index = rowsEls.indexOf(target.closest('[data-perf-row]') as HTMLElement);
    const forward = e.key === 'ArrowDown' || e.key === 'ArrowRight';
    const next = index === -1 ? 0 : (index + (forward ? 1 : -1) + rowsEls.length) % rowsEls.length;
    rowsEls[next]?.focus();
  };

  const toggleCompare = (id: string) => {
    setCompareIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 4) next.add(id);
      return next;
    });
  };
  const compared = rows.filter((r) => compareIds.has(r.id));

  const headerNode: PerfNode | null =
    scope.level === 4 ? projectNode : scope.level === 3 ? locationNode : scope.level === 2 ? stateNode : null;

  const crumb = (label: string, level: 1 | 2 | 3 | 'current') => {
    const isCurrent = level === 'current';
    return (
      <button
        type="button"
        disabled={isCurrent}
        onClick={() => !isCurrent && actions.drillToLevel(level)}
        className={`rounded-lg px-1.5 py-0.5 text-[15px] font-extrabold tracking-tight transition ${
          isCurrent ? 'cursor-default' : 'hover:bg-[rgba(11,59,46,0.06)]'
        }`}
        style={{ color: isCurrent ? OMS_COLORS.tealDark : '#1F5A47' }}
        aria-current={isCurrent ? 'page' : undefined}
      >
        {label}
      </button>
    );
  };
  const crumbSep = <ChevronRight aria-hidden className="h-3.5 w-3.5 shrink-0" style={{ color: OMS_COLORS.champagneDeep }} />;

  return (
    <section aria-label="Performance explorer" className={`${CARD_CLASS} p-4`} onKeyDown={onKeyDown}>
      {/* Engraved banknote inner rule */}
      <div aria-hidden className="pointer-events-none absolute inset-[7px] rounded-[11px] border border-dashed border-[rgba(11,59,46,0.14)]" />

      <div className="relative">
        {/* Masthead: breadcrumb + sort + level export */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className={EYEBROW_CLASS}>Performance explorer</p>
            <nav aria-label="Drill-down breadcrumb" className="mt-1 flex flex-wrap items-center gap-1">
              {crumb('India', scope.level === 1 ? 'current' : 1)}
              {scope.state && (
                <>
                  {crumbSep}
                  {crumb(scope.state, scope.level === 2 ? 'current' : 2)}
                </>
              )}
              {scope.locationId && (
                <>
                  {crumbSep}
                  {crumb(locationNode?.name ?? 'Location', scope.level === 3 ? 'current' : 3)}
                </>
              )}
              {scope.level === 4 && (
                <>
                  {crumbSep}
                  <span className="px-1.5 py-0.5 text-[15px] font-extrabold tracking-tight" style={{ color: OMS_COLORS.tealDark }} aria-current="page">
                    {projectNode?.name ?? 'Project'}
                  </span>
                </>
              )}
            </nav>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {scope.level > 1 && (
              <button
                type="button"
                onClick={actions.drillUp}
                className="flex items-center gap-1 rounded-lg border border-[rgba(11,59,46,0.24)] px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide transition hover:bg-[rgba(11,59,46,0.05)]"
                style={{ color: '#1F5A47', backgroundColor: '#fcfaf3' }}
              >
                <CornerLeftUp className="h-3 w-3" /> Up one level
              </button>
            )}
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              aria-label="Sort entities"
              className="rounded-xl border border-[rgba(11,59,46,0.24)] bg-[#fcfaf3] px-2 py-1.5 text-xs font-bold text-[#1F5A47] outline-none focus:border-[#1F5A47]"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {showDailyReport && (
              <ScopedDailyReportButton
                scope={{
                  state: scope.state,
                  locationId: scope.locationId,
                  projectId: scope.projectId,
                }}
                scopeLabel={
                  scope.level === 4
                    ? projectNode?.name ?? 'this project'
                    : scope.level === 3
                      ? locationNode?.name ?? 'this location'
                      : scope.level === 2
                        ? scope.state ?? 'this state'
                        : 'all locations & projects'
                }
              />
            )}
            <ExportMenu getPayload={buildReport} variant="quiet" />
          </div>
        </div>

        {/* Level header plate — the morph target of the signature drill-down. */}
        {headerNode && (
          <div className="mt-3 flex flex-wrap items-center gap-4 rounded-xl border border-[rgba(11,59,46,0.18)] px-4 py-3" style={{ backgroundColor: '#F8F2E2' }}>
            <ScoreDial score={headerNode.score} size={64} label={`Health score for ${headerNode.name}`} />
            <motion.div layoutId={`plate-${headerNode.id}`} className="min-w-0">
              <h3 className="truncate text-xl font-extrabold tracking-tight" style={{ color: OMS_COLORS.tealDark }}>
                {headerNode.name}
              </h3>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className="text-[9px] font-bold uppercase tracking-[0.12em]" style={{ color: OMS_COLORS.caption }}>
                  Health score
                </span>
                <BandChip score={headerNode.score} />
                <DeltaChip delta={headerNode.delta} phrase={`pts ${phrase}`} />
                {headerNode.score && <ScoreExplainer score={headerNode.score} />}
              </div>
            </motion.div>
            {/* Divider keeps the score block visually apart from the counts block. */}
            <div className="ml-auto flex flex-wrap gap-x-5 gap-y-1 border-l border-[rgba(11,59,46,0.14)] pl-5">
              <StatCount label="Completed" value={headerNode.completed} of={headerNode.scheduled} noun="tasks" size="sm" />
              <StatCount
                label="Overdue"
                value={headerNode.overdue}
                of={headerNode.open}
                noun="open tasks"
                size="sm"
                ink={headerNode.overdue > 0 ? '#9F1239' : OMS_COLORS.tealDark}
              />
              {headerNode.completed > 0 && (
                <StatCount
                  label="Pass rate"
                  value={headerNode.counts.pass}
                  valueText={`${Math.round((headerNode.counts.pass / headerNode.completed) * 100)}%`}
                  noun={`of ${formatNumber(headerNode.completed)} completed`}
                  size="sm"
                />
              )}
            </div>
          </div>
        )}

        {/* Level body + rails */}
        {loading && rows.length === 0 ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="h-40 animate-pulse rounded-2xl bg-[rgba(11,59,46,0.06)]" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="py-12 text-center text-sm font-medium" style={{ color: OMS_COLORS.caption }}>
            {q
              ? `Nothing in ${LEVEL_LABEL[scope.level].toLowerCase()} matches “${scope.query}” — clear the search to see everything in scope.`
              : 'No activity in this window — try a longer time range from the scope bar.'}
          </p>
        ) : (
          <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_250px]">
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.div
                key={levelKey}
                initial={{ opacity: 0, scale: 0.985, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 1.01, y: -8 }}
                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                className="min-w-0"
              >
                {(scope.level === 1 || scope.level === 2) && (
                  <LocationCards
                    rows={rows as never}
                    model={model}
                    deltaPhrase={phrase}
                    compareIds={compareIds}
                    onToggleCompare={toggleCompare}
                    onDrill={(row) => {
                      if (row.kind === 'state') actions.drillToState(row.name);
                      else actions.drillToLocation(row.id);
                    }}
                  />
                )}
                {scope.level === 3 && (
                  <ProjectLeaderboard
                    rows={rows as never}
                    deltaPhrase={phrase}
                    expanded={expanded}
                    onExpand={() => setExpanded(true)}
                    parentName={locationNode?.name ?? 'this location'}
                    compareIds={compareIds}
                    onToggleCompare={toggleCompare}
                    onDrill={actions.drillToProject}
                    onOpenProject={onOpenProject}
                  />
                )}
                {scope.level === 4 && (
                  <EngineerLeaderboard
                    rows={rows as never}
                    deltaPhrase={phrase}
                    compareIds={compareIds}
                    onToggleCompare={toggleCompare}
                    onOpen={onOpenEngineer}
                  />
                )}
              </motion.div>
            </AnimatePresence>

            <PerfRails
              rows={rows}
              deltaPhrase={phrase}
              onSelect={(node) => {
                if (node.kind === 'state') actions.drillToState(node.name);
                else if (node.kind === 'location') actions.drillToLocation(node.id);
                else if (node.kind === 'project') actions.drillToProject(node.id);
                else onOpenEngineer(node);
              }}
            />
          </div>
        )}
      </div>

      <CompareTray
        nodes={compared}
        deltaPhrase={phrase}
        onRemove={toggleCompare}
        onClear={() => setCompareIds(new Set())}
      />
    </section>
  );
}
